/**
 * Sense → Action ledger, the screen itself. Slice 62. `ActionLedgerPage`
 * fetches; this draws, so `verify-sense-screens.tsx` can render it.
 *
 * Every decision about a finding, newest first: what the finding said when it
 * was decided, taken or dismissed, by whom, and its number then and now. "Now"
 * is a date until 30 days have passed — a before-and-after read the next
 * morning would credit a change to an action that has had no time to make one —
 * and a sentence where there is nothing to compare (a deleted segment has no
 * count; a holdout whose readout can no longer be drawn). Never a number
 * standing in for either.
 *
 * ## A LOG, NOT A TABLE — SLICE 66
 *
 * It was a four-column `DataTable`. It is now the Blueprint's `EventLog`: one
 * dated line per decision, newest first, because that is what the ledger is —
 * a record of things that happened, read top to bottom — and a table invited
 * sorting by columns whose order is the whole point. Every fact a column
 * carried is still a field on the line, and nothing was dropped to fit:
 *
 *   - Finding: the title as it read, the date (the log's own time column) and
 *     who decided, after "by".
 *   - Decision: the badge in the right-hand slot, "Acted on" or "Dismissed".
 *   - Then: the figure with its label, and the rupees at stake when decided.
 *   - Now: the due date, the figure, or the sentence — the `data-after` span.
 *
 * The icon is tinted brand for "Acted on" and left neutral for "Dismissed".
 * Neither is a status colour: deciding is not good or bad news, and the
 * ledger's only verdict is the then-and-now pair, in words and numbers.
 * Figures are sans and tabular, not mono, for the dotted-zero reason the
 * Blueprint gives.
 */
import type { SenseAction, SenseActions } from '@wend/shared';
import { Badge, EventLog, type EventLogItem } from '@wend/ui';
import { Check, Minus } from 'lucide-react';

import { formatCount, formatDate, formatRupees } from '../../lib/format';

/** A holdout's metric can be negative: its sign is the point, so it is always shown. */
function figure(action: SenseAction, value: number): string {
  if (action.kind !== 'holdout_no_lift') return formatCount(value);
  return value > 0 ? `+${formatCount(value)}` : value < 0 ? `−${formatCount(Math.abs(value))}` : '0';
}

function after(action: SenseAction): string {
  switch (action.after.state) {
    case 'due':
      return `Due ${formatDate(action.after.dueOn)}`;
    case 'measured':
      return figure(action, action.after.value);
    case 'not_comparable':
      return action.after.reason;
  }
}

export function LedgerContent({ ledger }: { ledger: SenseActions }) {
  if (ledger.actions.length === 0) {
    return (
      <p className="text-[0.92rem] text-muted" data-role="ledger-empty">
        Nothing decided yet. Taking or dismissing a finding on Today records it here.
      </p>
    );
  }

  const items: EventLogItem[] = ledger.actions.map((row) => ({
    key: row.id,
    when: formatDate(row.decidedAt),
    tone: row.decision === 'taken' ? 'brand' : 'neutral',
    icon: row.decision === 'taken' ? <Check /> : <Minus />,
    title: row.title,
    detail: (
      <span className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
        <span>
          Then <b className="font-semibold tabular-nums text-text">{figure(row, row.metric.value)}</b>{' '}
          {row.metric.label}
          {row.atStake !== null && <> · {formatRupees(row.atStake.valueMinor)} at stake</>}
        </span>
        <span>
          Now{' '}
          <span className="text-text" data-after={row.after.state}>
            {after(row)}
          </span>
        </span>
        {row.decidedBy !== null && <span>by {row.decidedBy}</span>}
      </span>
    ),
    /* "Acted on", not "Taken": pressing a finding's action records it, and for
       most findings that action opens a page — the ledger cannot know what was
       done there (review). */
    aside: <Badge tone={row.decision === 'taken' ? 'info' : 'neutral'}>{row.decision === 'taken' ? 'Acted on' : 'Dismissed'}</Badge>,
  }));

  return <EventLog label="Decisions about Sense findings, newest first" whenWidth="date" items={items} />;
}
